import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ASSETS from "../../../../app/config/assets";
import { FaArrowUpLong } from "react-icons/fa6";
import Button from "../../../../ui/atoms/buttons/Button";
import ChapterItem from "../../components/chapter-item/ChapterItem";
import CourseChallenge from "../../components/course-challenge/CourseChallenge";
import {
  onGetChaptersByCourseId,
  onSaveLessonReaded,
  onGetLessonAssessmentAvailable,
} from "../../services/course";
import {
  capitalizeAll,
  formatDuration,
  isEmpty,
  wait,
} from "../../../../core/utils/helpers";
import Loading from "../../../../ui/atoms/loading/Loading";
// import TypewriterHTML from "../../components/type-writter/TypewriterHTML";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { getMimeType } from "../../../../core/utils/common-validators";
import { challengeSlice } from "../../../../app/store/slices/challengeSlice";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const StudentCourseReading = () => {
  const { course_title } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("viewId");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const axiosPrivate = useAxiosPrivate(); // on page mounted

  const [isFetching, setIsFetching] = useState(true);

  const [isBoxDialog, setIsBoxDialog] = useState(false);
  const [boxData, setBoxData] = useState({});
  const [isToggleChallenge, setIsToggleChallenge] = useState(false);
  const [isToggleDiscussion, setIsToggleDiscussion] = useState(false);

  const [isRefresh, setIsRefresh] = useState(false);
  const [isAssessmentAvailable, setIsAssessmentAvailable] = useState(false);

  const [coursesData, setCoursesData] = useState([]);
  const [courseLearningProgress, setCourseLearningProgress] = useState(0);
  const [isButtonChallengeAccessible, setIsButtonChallengeAccessible] =
    useState(false);

  const [forceOpenChapterId, setForceOpenChapterId] = useState(null);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [currentReadingLesson, setCurrentReadingLesson] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [lessonStartTime, setLessonStartTime] = useState(null);
  const [canGoNext, setCanGoNext] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentLessonTiming, setCurrentLessonTiming] = useState(0);

  const currentConnectedStream = useSelector(
    (state) => state.setInitConf.currentConnectedStream,
  );

  const _sys_role = currentConnectedStream?.sys_role;
  const _stream_id = currentConnectedStream?.stream_id;
  const _inscription_id = currentConnectedStream?.inscription_id;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchChapters = async () => {
      try {
        await wait(1500);

        const response = await onGetChaptersByCourseId(
          axiosPrivate,
          { inscription_id: _inscription_id, course_id: courseId },
          signal,
        );
        const _data = response?.data;
        const _timing = _data?.maximum_timing / 4;

        const _learning_progress_percent = (
          (_data?.readed_timing / _data?.maximum_timing) *
          100
        ).toFixed(2);

        setCourseLearningProgress(_learning_progress_percent);

        setIsButtonChallengeAccessible(
          _data?.readed_timing >= _timing ? true : false,
        );
        const _data_activated = _data?.result.filter(
          (row) =>
            row.is_activated && !row.is_deleted && row?.lessons?.length > 0,
        );
        const transformed_data = _data_activated?.map((item) => ({
          ...item,
          lessons: item?.lessons?.filter(
            (lesson) => lesson?.is_activated && !lesson?.is_deleted,
          ),
        }));
        setCoursesData(transformed_data || []);

        if (isEmpty(currentReadingLesson)) {
          const firstChapter = transformed_data?.[0];
          const firstLesson = firstChapter?.lessons?.[0];

          if (firstChapter && firstLesson) {
            setSelectedChapterId(firstChapter.id);
            setSelectedLessonId(firstLesson.id);
            setCurrentReadingLesson(firstLesson);

            // ouvrir le chapitre automatiquement
            setForceOpenChapterId(firstChapter.id);

            // initialiser le timer
            initLessonTimer(firstLesson);

            // fonction centralisée de sélection
            selectLesson(firstChapter.id, firstLesson);
          }
        }

        setIsFetching(false);
      } catch (err) {
        if (err.name === "AbortError") return; // requête annulée
        console.error(err);
      }
    };

    fetchChapters();

    return () => {
      controller.abort();
    };
  }, [isRefresh]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchAssessment = async () => {
      try {
        const response = await onGetLessonAssessmentAvailable(
          axiosPrivate,
          { inscription_id: _inscription_id },
          signal,
        );
        const _data = response?.data;

        if (_data?.status) {
          setIsBoxDialog(true);
          setBoxData({
            assessment_id: _data?.assessment_id,
            assessment_type: _data?.assessment_type,
            assessment_mode: _data?.assessment_mode,
            assessment_invitation_id: _data?.assessment_invitation_id,
          });
        }
      } catch (err) {
        if (err.name === "AbortError") return; // requête annulée
        console.error(err);
      }
    };

    fetchAssessment();

    return () => {
      controller.abort();
    };
  }, [isAssessmentAvailable]);

  const onToggleChallenge = () => {
    setIsToggleChallenge(!isToggleChallenge);
  };

  const onToggleDiscussion = () => {
    setIsToggleDiscussion(!isToggleDiscussion);
  };

  useEffect(() => {
    if (!lessonStartTime || currentLessonTiming === 0) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lessonStartTime) / 1000);

      const timeLeft = currentLessonTiming - elapsed;

      if (currentReadingLesson?.is_locked) {
        if (timeLeft <= 0) {
          setRemainingTime(0);
          setCanGoNext(true);
          clearInterval(interval);
        } else {
          setRemainingTime(timeLeft);
        }
      } else {
        setCanGoNext(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lessonStartTime, currentLessonTiming]);

  const chapters = coursesData || [];
  const currentChapterIndex = chapters.findIndex(
    (c) => c.id === selectedChapterId,
  );
  const currentLessonIndex = chapters[currentChapterIndex]?.lessons?.findIndex(
    (l) => l.id === selectedLessonId,
  );
  const isFirstLesson = currentChapterIndex === 0 && currentLessonIndex === 0;

  const handleNext = async () => {
    if (!canGoNext) return;

    setIsLoading(true); // start loader

    const currentChapter = chapters[currentChapterIndex];
    const lessons = currentChapter.lessons;

    if (currentReadingLesson?.is_locked) {
      const localData = {
        course_id: courseId,
        lesson_id: selectedLessonId,
        inscription_id: _inscription_id,
        dates: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
      };

      const { data } = await onSaveLessonReaded(axiosPrivate, localData);

      if (data?.status) {
        setIsRefresh((prev) => !prev);
      }
    }

    setIsAssessmentAvailable((prev) => !prev);

    const goToLesson = (lesson, chapterId) => {
      setTimeout(() => {
        setSelectedLessonId(lesson.id);
        setCurrentReadingLesson(lesson);
        initLessonTimer(lesson);
        setForceOpenChapterId(chapterId);
        setIsLoading(false); // stop loader
      }, 1500); // durée du loading
    };

    if (currentLessonIndex < lessons.length - 1) {
      goToLesson(lessons[currentLessonIndex + 1], currentChapter.id);
      return;
    }

    const nextChapter = chapters[currentChapterIndex + 1];
    if (nextChapter && nextChapter.lessons?.length > 0) {
      goToLesson(nextChapter.lessons[0], nextChapter.id);
    }
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handlePrevious = async () => {
    setIsLoading(true);

    await delay(1500);

    const currentChapter = chapters[currentChapterIndex];
    const lessons = currentChapter.lessons;

    if (currentLessonIndex > 0) {
      const prevLesson = lessons[currentLessonIndex - 1];
      setSelectedLessonId(prevLesson.id);
      setCurrentReadingLesson(prevLesson);
      initLessonTimer(prevLesson);
      setForceOpenChapterId(currentChapter.id);
    } else {
      const prevChapter = chapters[currentChapterIndex - 1];
      if (prevChapter && prevChapter.lessons?.length > 0) {
        const lastLesson = prevChapter.lessons[prevChapter.lessons.length - 1];
        setSelectedChapterId(prevChapter.id);
        setSelectedLessonId(lastLesson.id);
        setCurrentReadingLesson(lastLesson);
        initLessonTimer(lastLesson);
        setForceOpenChapterId(prevChapter.id);
      }
    }

    setIsLoading(false);
  };

  const initLessonTimer = (lesson) => {
    const timingInSeconds = Number(lesson.timing) || 0;

    setCurrentLessonTiming(timingInSeconds);
    setLessonStartTime(Date.now());
    setRemainingTime(timingInSeconds);
    setCanGoNext(false);
  };

  const selectLesson = (chapterId, lesson) => {
    setSelectedChapterId(chapterId);
    setSelectedLessonId(lesson.id);
    setCurrentReadingLesson(lesson);

    initLessonTimer(lesson);
  };

  return (
    <React.Fragment>
      {isFetching ? (
        <Loading type={"type-2"} />
      ) : (
        <div className="student-course-reading">
          <div className="s-c-r-tile tile-1">
            <div className="tile-1-head">
              <div className="w-full d-flex gap-md">
                <div className="w-20">
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"⬅️ Back"}
                    action={() => navigate(-1)}
                  />
                </div>
                <div className="w-80 learning-progress">
                  <span>Course learning progress</span>
                  <div className="l-p-container gap-xs">
                    <div className="l-p-wrapper">
                      <div
                        className="l-p-bar"
                        style={{ width: `${courseLearningProgress}%` }}
                      ></div>
                    </div>
                    <p>{`${courseLearningProgress}%`}</p>
                  </div>
                </div>
              </div>
              <span>Course of :</span>
              <h1>{capitalizeAll(course_title)}</h1>
            </div>
            <div className="tile-1-content">
              {coursesData?.map((chapter) => {
                return (
                  <ChapterItem
                    key={chapter.id}
                    chapter={chapter}
                    selectedChapterId={selectedChapterId}
                    selectedLessonId={selectedLessonId}
                    onSelectLesson={(chapterId, lesson) => {
                      setSelectedChapterId(chapterId);
                      setSelectedLessonId(lesson.id);
                      setCurrentReadingLesson(lesson);
                      initLessonTimer(lesson);

                      setForceOpenChapterId(chapterId);
                    }}
                    forceOpenChapterId={forceOpenChapterId}
                  />
                );
              })}
            </div>
          </div>
          <div className="s-c-r-tile tile-2">
            <div className="tile-2-head">
              {isButtonChallengeAccessible && (
                <div className="w-20">
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"❓Challenge"}
                    action={() => onToggleChallenge()}
                  />
                </div>
              )}
              <div className="w-20">
                <Button
                  type={"button"}
                  preferedStyle={"default"}
                  title={"💬 Talk to My Assisstant"}
                  action={() => onToggleDiscussion()}
                />
              </div>
            </div>
            <div className="tile-2-content">
              {isLoading ? (
                <div className="w-full h-full d-flex align-center justify-center">
                  <Loading type={"type-2"} />
                </div>
              ) : (
                currentReadingLesson?.lessons_sections?.map(
                  (lesson_section, ls_idx) => {
                    const groupedAssets =
                      lesson_section?.lesson_sections_assets?.reduce(
                        (acc, item) => {
                          acc[item.type] = acc[item.type] || [];
                          acc[item.type].push(item);
                          return acc;
                        },
                        {},
                      );
                    const assets_audios = groupedAssets.audio || [];
                    const assets_videos = groupedAssets.video || [];
                    const assets_images = groupedAssets.image || [];

                    return (
                      <React.Fragment key={ls_idx}>
                        <div
                          className="ql-editor"
                          style={{ height: "auto" }}
                          dangerouslySetInnerHTML={{
                            __html: lesson_section?.description,
                          }}
                          key={ls_idx}
                        />

                        {/* <TypewriterHTML
                        key={ls_idx}
                        html={lesson_section?.description}
                      /> */}

                        {assets_audios?.length > 0 && (
                          <div className="tile-2-content-audios">
                            {assets_audios?.map((audio, a_k) => {
                              return (
                                <div className="content-audios-item" key={a_k}>
                                  <audio
                                    controls
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  >
                                    <source
                                      src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/audios/lessons/${audio?.title}`}
                                      type={getMimeType(audio?.title)}
                                    />
                                  </audio>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {assets_videos?.length > 0 && (
                          <div className="tile-2-content-videos">
                            {assets_videos?.map((video, v_k) => {
                              return (
                                <div className="content-videos-item" key={v_k}>
                                  <video
                                    controls
                                    preload="metadata"
                                    width={"100%"}
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  >
                                    <source
                                      src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/videos/lessons/${video?.title}`}
                                      type={getMimeType(video?.title)}
                                    />
                                    Your browser does not support the video
                                    element.
                                  </video>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {assets_images?.length > 0 && (
                          <div className="tile-2-content-images">
                            {assets_images?.map((image, i_k) => {
                              return (
                                <div className="content-images-item" key={i_k}>
                                  <img
                                    src={`${process.env.REACT_APP_API_SERVER_URL}:${process.env.REACT_APP_API_SERVER_PORT}/storage/images/lessons/${image?.title}`}
                                    alt={`${image?.title}`}
                                    controlsList="nodownload"
                                    onContextMenu={(e) => e.preventDefault()}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  },
                )
              )}
            </div>
            <div className="tile-2-foot">
              <div className="w-20">
                <Button
                  type="button"
                  preferedStyle="default"
                  title="⬅️ Previous Lesson"
                  action={handlePrevious}
                  disabled={isFirstLesson}
                />
              </div>
              <div className="w-20">
                <Button
                  type="button"
                  preferedStyle="default"
                  title={
                    canGoNext
                      ? "Next Lesson ➡️"
                      : `⏳ ${formatDuration(remainingTime)} remain`
                  }
                  action={handleNext}
                  disabled={!canGoNext}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Box Dialog */}
      <div
        className={isBoxDialog ? "box-dialog slide-in" : "box-dialog slide-out"}
      >
        <div className="box-dialog-wrapper">
          <h1>{capitalizeAll(boxData?.assessment_type)} Time!</h1>
          <img src={ASSETS?.icon_feedback_gif} alt="assessment time" />
          <p>
            You have {capitalizeAll(boxData?.assessment_type)} to complete as
            your next journey activity.
          </p>
          <Button
            title={`#️⃣ Click to Join ${capitalizeAll(boxData?.assessment_type)}`}
            type={"button"}
            preferedStyle={"normal"}
            action={() => {
              const _assessment_mode = boxData?.assessment_mode;
              const _assessment_id = boxData?.assessment_id;
              const _assessment_invitation_id =
                boxData?.assessment_invitation_id;

              dispatch(
                challengeSlice?.actions?.setLessonAssessment({
                  assessment_type:
                    boxData?.assessment_type === "quiz" ? "QUIZ" : "LA", // LA stand for Lesson Assessment
                  assessment_mode: _assessment_mode,
                  assessment_id: _assessment_id,
                  assessment_invitation_id: _assessment_invitation_id,
                }),
              );

              const _to = `/${_sys_role}/lessons/assessment`;
              navigate(_to, { replace: true });
            }}
          />
        </div>
      </div>
      {/* Assessment */}
      <div
        className={
          isToggleChallenge
            ? "assessment-stage slide-in"
            : "assessment-stage slide-out"
        }
      >
        <div className="assessment-stage-wrapper">
          <div className="stage-head w-full">
            <div className="w-fit-content">
              <h1>♻️ Setting a new assessment</h1>
              <p>Requesting assessment based from the lessons learned</p>
            </div>
            <Button
              type={"button"}
              preferedStyle={"default"}
              title={"❌ Close"}
              action={() => onToggleChallenge()}
            />
          </div>
          <div className="stage-container">
            {isToggleChallenge && (
              <CourseChallenge
                stream_id={_stream_id}
                inscription_id={_inscription_id}
                course_id={courseId}
                lesson_id={selectedLessonId}
              />
            )}
          </div>
        </div>
      </div>
      {/* Discussion */}
      <div
        className={
          isToggleDiscussion
            ? "course-discussion-stage slide-in"
            : "course-discussion-stage slide-out"
        }
      >
        <div className="stage-head w-full">
          <div className="assisstant">
            <img src={ASSETS?.icon_assistance_png} alt="my ai assisstant" />
            <div className="d-flex flex-col">
              <h1>Hello, I am your Assisstant.</h1>
              <p>Tell me how can i hell you today ?</p>
            </div>
          </div>
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"💬 Close My Assistant"}
            action={() => onToggleDiscussion()}
          />
        </div>
        <div className="stage-container">
          <div className="ai-response">
            Below is a professional assessment set of 20 questions for the
            course Introduction to Management, specifically for the chapter
            “Introduction to Management.” The questions mix knowledge,
            comprehension, analysis, and applied thinking, and measure both
            cognitive and psychological skills such as decision-making, critical
            thinking, leadership awareness, and organizational understanding.
          </div>
          <div className="user-question">
            Below is a professional assessment set of 20 questions for the
            course Introduction to Management, specifically for the chapter
            “Introduction to Management.” The questions mix knowledge,
            comprehension, analysis, and applied thinking, and measure both
            cognitive and psychological skills such as decision-making, critical
            thinking, leadership awareness, and organizational understanding.
          </div>
        </div>
        <div className="stage-foot">
          <textarea placeholder="Type your question"></textarea>
          <span>
            <FaArrowUpLong className="icon" />
          </span>
        </div>
      </div>
    </React.Fragment>
  );
};

export default StudentCourseReading;
